import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchResidentPaymentMethods } from '../services/Payments';
import { ResidentPaymentMethod } from '../types/Payments';

type UseResidentOrderCardEligibilityParams = {
    residentId?: string;
    facilityId?: string;
    enabled?: boolean;
};

type CardEligibilitySnapshot = {
    hasDefaultCard: boolean;
    // Any saved card at all, so the gate can say "set a default" instead of "add a card".
    hasAnyCard: boolean;
    // The default card is past its expiry month, so it counts as no default card.
    defaultCardExpired: boolean;
    lookupFailed: boolean;
};

type CardEligibilityState = CardEligibilitySnapshot & {
    loading: boolean;
};

export type ResidentOrderCardEligibility = CardEligibilityState & {
    refresh: () => Promise<void>;
};

type CachedEligibilityState = {
    value: CardEligibilitySnapshot;
    expiresAt: number;
};

const DEFAULT_ELIGIBILITY_SNAPSHOT: CardEligibilitySnapshot = {
    hasDefaultCard: false,
    hasAnyCard: false,
    defaultCardExpired: false,
    lookupFailed: false,
};

const CACHE_TTL_MS = 60 * 1000;
const EVENT_REVALIDATION_COOLDOWN_MS = 1000;
const eligibilityCache = new Map<string, CachedEligibilityState>();
const eligibilityInFlight = new Map<string, Promise<CardEligibilitySnapshot>>();
// Bumped when a card is added/removed/set as default or a fresh check is forced. A lookup
// that started under an older generation is stale: its result is not cached or shown.
const eligibilityGeneration = new Map<string, number>();

const getCacheKey = (residentId: string, facilityId: string): string => `${residentId}:${facilityId}`;
const getGeneration = (cacheKey: string): number => eligibilityGeneration.get(cacheKey) || 0;
const bumpGeneration = (cacheKey: string): number => {
    const next = getGeneration(cacheKey) + 1;
    eligibilityGeneration.set(cacheKey, next);
    return next;
};

export const invalidateResidentOrderCardEligibility = (residentId?: string, facilityId?: string): void => {
    if (!residentId || !facilityId) return;
    const cacheKey = getCacheKey(residentId, facilityId);
    eligibilityCache.delete(cacheKey);
    eligibilityInFlight.delete(cacheKey);
    bumpGeneration(cacheKey);
};

// A card works through the last day of its expiry month. A card without an expiry is treated as valid.
const isCardExpired = (method: ResidentPaymentMethod, now: Date = new Date()): boolean => {
    const expMonth = Number(method.card?.expMonth);
    const expYear = Number(method.card?.expYear);
    if (!expMonth || !expYear) return false;
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    return expYear < currentYear || (expYear === currentYear && expMonth < currentMonth);
};

export const resolveHasDefaultCard = (methods: ResidentPaymentMethod[]): boolean => {
    return methods.some((method) => Boolean(method && method.isDefault && !isCardExpired(method)));
};

const resolveDefaultCardExpired = (methods: ResidentPaymentMethod[]): boolean => {
    return methods.some((method) => Boolean(method && method.isDefault && isCardExpired(method)));
};

const fetchEligibilitySnapshot = async (
    residentId: string,
    facilityId: string,
): Promise<CardEligibilitySnapshot> => {
    try {
        const methods = await fetchResidentPaymentMethods(residentId, facilityId);
        const safeMethods = Array.isArray(methods) ? methods : [];
        return {
            hasDefaultCard: resolveHasDefaultCard(safeMethods),
            hasAnyCard: safeMethods.some(Boolean),
            defaultCardExpired: resolveDefaultCardExpired(safeMethods),
            lookupFailed: false,
        };
    } catch (_error) {
        return {
            hasDefaultCard: false,
            hasAnyCard: false,
            defaultCardExpired: false,
            lookupFailed: true,
        };
    }
};

const useResidentOrderCardEligibility = ({
    residentId = '',
    facilityId = '',
    enabled = true,
}: UseResidentOrderCardEligibilityParams): ResidentOrderCardEligibility => {
    const shouldCheck = Boolean(enabled && residentId && facilityId);
    const [state, setState] = useState<CardEligibilityState>({
        ...DEFAULT_ELIGIBILITY_SNAPSHOT,
        loading: shouldCheck,
    });
    const latestSnapshotRef = useRef<CardEligibilitySnapshot>(DEFAULT_ELIGIBILITY_SNAPSHOT);
    const isMountedRef = useRef<boolean>(true);
    const lastEventRevalidationAtRef = useRef<number>(0);
    // Only the latest load of this hook may set its state (e.g. after switching resident).
    const loadSeqRef = useRef<number>(0);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const loadEligibility = useCallback(async (forceRefresh = false): Promise<void> => {
        loadSeqRef.current += 1;
        const loadSeq = loadSeqRef.current;
        const isLatestLoad = () => isMountedRef.current && loadSeqRef.current === loadSeq;
        if (!shouldCheck) {
            latestSnapshotRef.current = DEFAULT_ELIGIBILITY_SNAPSHOT;
            if (isMountedRef.current) {
                setState({
                    ...DEFAULT_ELIGIBILITY_SNAPSHOT,
                    loading: false,
                });
            }
            return;
        }

        const cacheKey = getCacheKey(residentId, facilityId);
        const currentTime = Date.now();
        const cachedValue = eligibilityCache.get(cacheKey);

        if (!forceRefresh && cachedValue && cachedValue.expiresAt > currentTime) {
            latestSnapshotRef.current = cachedValue.value;
            if (isMountedRef.current) {
                setState({
                    ...cachedValue.value,
                    loading: false,
                });
            }
            return;
        }

        if (isMountedRef.current) {
            setState((previousState) => ({
                ...previousState,
                loading: true,
            }));
        }

        // Retry always starts a fresh check rather than joining one already running.
        if (forceRefresh) {
            eligibilityInFlight.delete(cacheKey);
            bumpGeneration(cacheKey);
        }
        const generation = getGeneration(cacheKey);
        let inFlightRequest = eligibilityInFlight.get(cacheKey);
        if (!inFlightRequest) {
            inFlightRequest = fetchEligibilitySnapshot(residentId, facilityId);
            eligibilityInFlight.set(cacheKey, inFlightRequest);
        }

        try {
            const fetchedSnapshot = await inFlightRequest;
            if (getGeneration(cacheKey) !== generation) {
                // Cards changed while this check ran; ask again for the current answer.
                if (isLatestLoad()) await loadEligibility(false);
                return;
            }
            // A failed check is not remembered, so the next screen asks again.
            if (!fetchedSnapshot.lookupFailed) {
                eligibilityCache.set(cacheKey, {
                    value: fetchedSnapshot,
                    expiresAt: Date.now() + CACHE_TTL_MS,
                });
            }
            if (!isLatestLoad()) return;
            latestSnapshotRef.current = fetchedSnapshot;

            if (isMountedRef.current) {
                setState({
                    ...fetchedSnapshot,
                    loading: false,
                });
            }
        } catch (_error) {
            if (!isLatestLoad()) return;
            const fallbackSnapshot: CardEligibilitySnapshot = {
                hasDefaultCard: false,
                hasAnyCard: false,
                defaultCardExpired: false,
                lookupFailed: true,
            };
            latestSnapshotRef.current = fallbackSnapshot;

            if (isMountedRef.current) {
                setState({
                    ...fallbackSnapshot,
                    loading: false,
                });
            }
        } finally {
            const currentRequest = eligibilityInFlight.get(cacheKey);
            if (currentRequest === inFlightRequest) {
                eligibilityInFlight.delete(cacheKey);
            }
        }
    }, [facilityId, residentId, shouldCheck]);

    useEffect(() => {
        loadEligibility(false).catch(() => {
            if (isMountedRef.current) {
                setState({
                    ...latestSnapshotRef.current,
                    lookupFailed: true,
                    loading: false,
                });
            }
        });
    }, [loadEligibility]);

    const refresh = useCallback(async () => {
        await loadEligibility(true);
    }, [loadEligibility]);

    useEffect(() => {
        if (!shouldCheck) return undefined;

        const refreshFromEvent = () => {
            const currentTime = Date.now();
            if (currentTime - lastEventRevalidationAtRef.current < EVENT_REVALIDATION_COOLDOWN_MS) {
                return;
            }
            lastEventRevalidationAtRef.current = currentTime;
            loadEligibility(true).catch(() => {
                if (isMountedRef.current) {
                    setState((previousState) => ({
                        ...previousState,
                        lookupFailed: true,
                        loading: false,
                    }));
                }
            });
        };

        const handleFocus = () => {
            refreshFromEvent();
        };

        const handlePageShow = () => {
            refreshFromEvent();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshFromEvent();
            }
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('pageshow', handlePageShow);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('pageshow', handlePageShow);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [loadEligibility, shouldCheck]);

    return {
        ...state,
        refresh,
    };
};

export default useResidentOrderCardEligibility;
